<template>
  <v-dialog
    v-model="dialogProxy"
    :max-width="showPreview ? 'calc(100% - 80px)' : '1200px'"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="headline d-flex align-center">
        <span>Technical Handover Report</span>
        <v-spacer />

        <v-btn
          v-if="showPreview"
          icon
          class="mr-2"
          @click="showPreview = false"
        >
          <v-icon>mdi-eye-off</v-icon>
        </v-btn>

        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <div class="technical-dialog-layout">
          <div :class="['technical-form-pane', showPreview ? 'with-preview' : '']">
            <div class="pa-6">
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model="form.subject"
                    label="Subject *"
                    outlined
                    dense
                    :error-messages="getValidationError('subject')"
                    @input="clearValidationError('subject')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-text-field
                    v-model="form.work_name"
                    label="Work Name *"
                    outlined
                    dense
                    :error-messages="getValidationError('work_name')"
                    @input="clearValidationError('work_name')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Attendees</div>

              <v-alert
                v-if="hasOptionalWarning('attendees')"
                dense
                text
                type="warning"
                class="mb-3"
              >
                Attendees is empty.
              </v-alert>

              <v-row
                v-for="(attendee, index) in form.attendees"
                :key="'attendee-' + index"
                dense
                class="mb-1"
              >
                <v-col cols="11">
                  <v-text-field
                    v-model="form.attendees[index]"
                    :label="`Attendee #${index + 1}`"
                    outlined
                    dense
                    hide-details
                    :class="{ 'optional-warning-field': hasAttendeeOptionalWarning(index) }"
                    @input="clearAttendeeOptionalWarning(index)"
                  />
                </v-col>
                <v-col cols="1" class="d-flex align-center justify-center">
                  <v-btn icon color="red" @click="removeAttendee(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <v-btn small outlined color="primary" class="mb-4" @click="addAttendee">
                <v-icon left>mdi-plus</v-icon>
                Add Attendee
              </v-btn>

              <div class="section-title mb-2">Customer Information</div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.customer_name"
                    label="Customer Name *"
                    outlined
                    dense
                    :error-messages="getValidationError('customer_name')"
                    @input="clearValidationError('customer_name')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.customer_representative"
                    label="Customer Representative"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('customer_representative') }"
                    @input="clearOptionalWarning('customer_representative')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.customer_address"
                    label="Customer Address *"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :error-messages="getValidationError('customer_address')"
                    @input="clearValidationError('customer_address')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.shipping_address"
                    label="Shipping address"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :class="{ 'optional-warning-field': hasOptionalWarning('shipping_address') }"
                    @input="clearOptionalWarning('shipping_address')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Contractor Information</div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.contractor_name"
                    label="Contractor Name *"
                    outlined
                    dense
                    :error-messages="getValidationError('contractor_name')"
                    @input="clearValidationError('contractor_name')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.contractor_representative"
                    label="Contractor Representative"
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasOptionalWarning('contractor_representative') }"
                    @input="clearOptionalWarning('contractor_representative')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.contractor_address"
                    label="Contractor Address *"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :error-messages="getValidationError('contractor_address')"
                    @input="clearValidationError('contractor_address')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Customer Statement</div>

              <v-row dense>
                <v-col cols="12">
                  <v-textarea
                    v-model="form.completion_statement"
                    label="Completion Statement *"
                    outlined
                    dense
                    rows="2"
                    auto-grow
                    :error-messages="getValidationError('completion_statement')"
                    @input="clearValidationError('completion_statement')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.completion_followup_text"
                    label="Additional text"
                    outlined
                    dense
                    rows="3"
                    auto-grow
                    :class="{ 'optional-warning-field': hasOptionalWarning('completion_followup_text') }"
                    @input="clearOptionalWarning('completion_followup_text')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Items</div>

              <v-alert
                v-if="getValidationError('items').length"
                dense
                text
                type="warning"
                class="mb-3"
              >
                {{ getValidationError('items')[0] }}
              </v-alert>

              <v-row
                v-for="(item, index) in form.items"
                :key="'item-' + index"
                dense
                class="mb-2"
              >
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="item.name"
                    label="Name *"
                    hide-details="auto"
                    outlined
                    dense
                    :error-messages="getItemValidationError(index, 'name')"
                    @input="clearItemValidationError(index, 'name')"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="item.quantity"
                    label="Quantity *"
                    hide-details="auto"
                    outlined
                    dense
                    :error-messages="getItemValidationError(index, 'quantity')"
                    @input="clearItemValidationError(index, 'quantity')"
                  />
                </v-col>

                <v-col cols="12" md="2">
                  <v-text-field
                    v-model="item.unit"
                    label="Unit"
                    hide-details
                    outlined
                    dense
                    :class="{ 'optional-warning-field': hasItemOptionalWarning(index, 'unit') }"
                    @input="clearItemOptionalWarning(index, 'unit')"
                  />
                </v-col>

                <v-col cols="12" md="2" class="d-flex align-center">
                  <v-btn icon color="red" @click="removeItem(index)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <v-btn small outlined color="primary" class="mb-4" @click="addItem">
                <v-icon left>mdi-plus</v-icon>
                Add item
              </v-btn>

              <div class="section-title mt-4 mb-2">Contractor's Statement</div>

              <v-row dense>
                <v-col cols="12">
                  <v-textarea
                    v-model="form.contractor_statement"
                    label="Contractor's Statement *"
                    outlined
                    dense
                    rows="3"
                    auto-grow
                    :error-messages="getValidationError('contractor_statement')"
                    @input="clearValidationError('contractor_statement')"
                  />
                </v-col>

                <v-col cols="12">
                  <v-textarea
                    v-model="form.serial_number"
                    label="Serial Number"
                    outlined
                    dense
                    rows="3"
                    auto-grow
                    :class="{ 'optional-warning-field': hasOptionalWarning('serial_number') }"
                    @input="clearOptionalWarning('serial_number')"
                  />
                </v-col>
              </v-row>

              <div class="section-title mt-4 mb-2">Issue</div>

              <v-row dense>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="form.issue_place"
                    label="Issue Place *"
                    outlined
                    dense
                    :error-messages="getValidationError('issue_place')"
                    @input="clearValidationError('issue_place')"
                  />
                </v-col>

                <v-col cols="12" md="6">
                  <v-menu
                    v-model="issueDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.issue_date"
                        label="Issue Date *"
                        outlined
                        dense
                        readonly
                        :error-messages="getValidationError('issue_date')"
                        v-bind="attrs"
                        v-on="on"
                      />
                    </template>

                    <v-date-picker
                      v-model="form.issue_date"
                      no-title
                      scrollable
                      @input="onIssueDateSelected"
                    />
                  </v-menu>
                </v-col>
              </v-row>
            </div>
          </div>

          <div v-if="showPreview" class="technical-preview-pane">
            <div class="technical-preview-toolbar">
              <div class="subtitle-2 font-weight-medium">Preview</div>

              <v-progress-circular
                v-if="previewLoading"
                indeterminate
                size="18"
                width="2"
                color="primary"
              />
            </div>

            <iframe
              v-if="previewHtml"
              :srcdoc="previewHtml"
              class="technical-preview-frame"
              frameborder="0"
            />

            <div v-else class="technical-preview-empty">
              No preview available.
            </div>
          </div>
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4">
        <v-btn
          text
          color="primary"
          :loading="previewLoading"
          @click="openPreview"
        >
          {{ showPreview ? "Hide Preview" : "Open Preview" }}
        </v-btn>

        <v-spacer />

        <v-btn text @click="closeDialog">Cancel</v-btn>

        <v-btn
          color="primary"
          :loading="loading"
          @click="generateDocument"
        >
          Generate
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import ApiService from "@/core/services/api.service";
import Swal from "sweetalert2";


const DOCUMENT_TYPE = "technical_handover_protocol";

export default {
  name: "TechnicalHandoverProtocolDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    salesOrderId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      previewLoading: false,
      showPreview: false,
      previewHtml: "",
      previewDebounceTimer: null,
      previewRequestId: 0,
      issueDateMenu: false,
      validationErrors: {},
      optionalWarnings: {},
      form: {
        subject: "",
        attendees: [],
        work_name: "",
        customer_name: "",
        customer_address: "",
        shipping_address: "",
        customer_representative: "",
        contractor_name: "",
        contractor_address: "",
        contractor_representative: "",
        completion_statement: "",
        completion_followup_text: "",
        items: [],
        contractor_statement: "",
        serial_number: null,
        issue_place: "Zagyvarékas",
        issue_date: "",
      },
    };
  },

  computed: {
    dialogProxy: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },

  watch: {
    value(val) {
      if (val) {
        this.loadDefaults();
      } else {
        this.resetPreview();
      }
    },

    form: {
      deep: true,
      handler() {
        if (!this.showPreview) {
          return;
        }

        this.schedulePreviewRefresh();
      },
    },
  },

  methods: {
    async loadDefaults() {
      try {
        const { data } = await ApiService.get(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/defaults`
        );

        this.form = {
          subject: data.subject || "",
          attendees: Array.isArray(data.attendees) ? data.attendees : [],
          work_name: data.work_name || "",
          customer_name: data.customer_name || "",
          customer_address: data.customer_address || "",
          shipping_address: data.shipping_address || "",
          customer_representative: data.customer_representative || "",
          contractor_name: data.contractor_name || "",
          contractor_address: data.contractor_address || "",
          contractor_representative: data.contractor_representative || "",
          completion_statement: data.completion_statement || "",
          completion_followup_text: data.completion_followup_text || "",
          items: Array.isArray(data.items) ? data.items.filter(item => !String(item.name).includes('Szállítási díj')) : [],
          contractor_statement: data.contractor_statement || "",
          serial_number: data.serial_number || null,
          issue_place: data.issue_place || "Zagyvarékas",
          issue_date: data.issue_date || "",
        };

        this.validationErrors = {};
        this.optionalWarnings = {};
      } catch (e) {
        console.log(e);
      }
    },

    isEmpty(value) {
      return value === null || value === undefined || String(value).trim() === "";
    },

    hasOptionalWarning(field) {
      return Boolean(this.optionalWarnings[field]);
    },

    hasAttendeeOptionalWarning(index) {
      return Boolean(this.optionalWarnings[this.getAttendeeValidationKey(index)]);
    },

    hasItemOptionalWarning(index, field) {
      return Boolean(this.optionalWarnings[this.getItemValidationKey(index, field)]);
    },

    clearOptionalWarning(field) {
      if (this.optionalWarnings[field]) {
        this.$delete(this.optionalWarnings, field);
      }
    },

    clearAttendeeOptionalWarning(index) {
      const key = this.getAttendeeValidationKey(index);

      if (this.optionalWarnings[key]) {
        this.$delete(this.optionalWarnings, key);
      }
    },

    clearItemOptionalWarning(index, field) {
      const key = this.getItemValidationKey(index, field);

      if (this.optionalWarnings[key]) {
        this.$delete(this.optionalWarnings, key);
      }
    },

    getValidationError(field) {
      return this.validationErrors[field] || [];
    },

    getAttendeeValidationKey(index) {
      return `attendees.${index}`;
    },

    getItemValidationKey(index, field) {
      return `items.${index}.${field}`;
    },

    getItemValidationError(index, field) {
      return this.validationErrors[this.getItemValidationKey(index, field)] || [];
    },

    clearValidationError(field) {
      if (this.validationErrors[field]) {
        this.$delete(this.validationErrors, field);
      }
    },

    clearItemValidationError(index, field) {
      const key = this.getItemValidationKey(index, field);

      if (this.validationErrors[key]) {
        this.$delete(this.validationErrors, key);
      }
    },

    validateForm() {
      const errors = {};
      const requiredFields = [
        { key: "subject", label: "Subject" },
        { key: "work_name", label: "Work Name" },
        { key: "customer_name", label: "Customer Name" },
        { key: "customer_address", label: "Customer Address" },
        { key: "contractor_name", label: "Contractor Name" },
        { key: "contractor_address", label: "Contractor Address" },
        { key: "completion_statement", label: "Completion Statement" },
        { key: "contractor_statement", label: "Contractor's Statement" },
        { key: "issue_place", label: "Issue Place" },
        { key: "issue_date", label: "Issue Date" },
      ];

      requiredFields.forEach((field) => {
        if (this.isEmpty(this.form[field.key])) {
          errors[field.key] = [`${field.label} is required.`];
        }
      });

      if (!Array.isArray(this.form.items) || this.form.items.length === 0) {
        errors.items = ["At least one item is required."];
      } else {
        this.form.items.forEach((item, index) => {
          [
            { key: "name", label: "Item name" },
            { key: "quantity", label: "Quantity" },
          ].forEach((field) => {
            if (this.isEmpty(item[field.key])) {
              errors[this.getItemValidationKey(index, field.key)] = [
                `${field.label} is required.`,
              ];
            }
          });
        });
      }

      this.validationErrors = errors;

      return Object.keys(errors).length === 0;
    },

    async showValidationAlert() {
      await Swal.fire({
        title: "Missing required data",
        icon: "warning",
        text: "Please fill in all required fields before creating the document.",
        confirmButtonText: "OK",
      });
    },

    getMissingOptionalFields() {
      const missingFields = [];
      const optionalFields = [
        { key: "customer_representative", label: "Customer Representative" },
        { key: "shipping_address", label: "Shipping Address" },
        { key: "contractor_representative", label: "Contractor Representative" },
        { key: "completion_followup_text", label: "Additional text" },
        { key: "serial_number", label: "Serial Number" },
      ];

      optionalFields.forEach((field) => {
        if (this.isEmpty(this.form[field.key])) {
          missingFields.push(field);
        }
      });

      if (!Array.isArray(this.form.attendees) || this.form.attendees.length === 0) {
        missingFields.push({
          key: "attendees",
          label: "Attendees",
        });
      } else {
        this.form.attendees.forEach((attendee, index) => {
          if (this.isEmpty(attendee)) {
            missingFields.push({
              key: this.getAttendeeValidationKey(index),
              label: `Attendee #${index + 1}`,
            });
          }
        });
      }

      if (Array.isArray(this.form.items)) {
        this.form.items.forEach((item, index) => {
          if (this.isEmpty(item.unit)) {
            missingFields.push({
              key: this.getItemValidationKey(index, "unit"),
              label: `Item ${index + 1} - Unit`,
            });
          }
        });
      }

      return missingFields;
    },

    markOptionalWarnings(fields) {
      const warnings = {};

      fields.forEach((field) => {
        warnings[field.key] = true;
      });

      this.optionalWarnings = warnings;
    },

    async confirmMissingOptionalFields() {
      const missingFields = this.getMissingOptionalFields();

      if (!missingFields.length) {
        this.optionalWarnings = {};
        return true;
      }

      const fieldList = missingFields
        .map((field) => `<li>${field.label}</li>`)
        .join("");

      const result = await Swal.fire({
        title: "Some fields are empty",
        icon: "warning",
        html: `The following fields are not filled:<br><ul style="text-align:left;margin-top:12px;">${fieldList}</ul><br>Do you want to continue anyway?`,
        showCancelButton: true,
        confirmButtonText: "Yes, generate document",
        cancelButtonText: "No, I will fill them",
        confirmButtonColor: "#00c300",
        width: "650px",
      });

      if (result.isConfirmed) {
        this.optionalWarnings = {};
        return true;
      }

      this.markOptionalWarnings(missingFields);
      return false;
    },

    onIssueDateSelected() {
      this.issueDateMenu = false;
      this.clearValidationError("issue_date");
    },

    addAttendee() {
      this.form.attendees.push("");
    },

    removeAttendee(index) {
      this.form.attendees.splice(index, 1);
    },

    addItem() {
      this.form.items.push({
        name: "",
        quantity: 1,
        unit: "db",
      });
    },

    removeItem(index) {
      this.form.items.splice(index, 1);
    },

    resetPreview() {
      this.showPreview = false;
      this.previewHtml = "";
      this.previewLoading = false;
      this.validationErrors = {};
      this.optionalWarnings = {};

      if (this.previewDebounceTimer) {
        clearTimeout(this.previewDebounceTimer);
        this.previewDebounceTimer = null;
      }
    },

    closeDialog() {
      this.resetPreview();
      this.dialogProxy = false;
    },

    schedulePreviewRefresh() {
      if (this.previewDebounceTimer) {
        clearTimeout(this.previewDebounceTimer);
      }

      this.previewDebounceTimer = setTimeout(() => {
        this.refreshPreview();
      }, 400);
    },

    async openPreview() {
      if(this.showPreview === true){
        this.showPreview = false;
      }else{
        this.showPreview = true;
      }

      await this.refreshPreview();
    },

    async refreshPreview() {
      const requestId = ++this.previewRequestId;
      this.previewLoading = true;

      try {
        const { data } = await ApiService.post(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/preview`,
          this.form
        );

        if (requestId !== this.previewRequestId) {
          return;
        }

        this.previewHtml = data.html || "";
      } catch (error) {
        console.log(error);
      } finally {
        if (requestId === this.previewRequestId) {
          this.previewLoading = false;
        }
      }
    },

    async generateDocument() {
      if (!this.validateForm()) {
        await this.showValidationAlert();
        return;
      }

      const missingFields = this.getMissingOptionalFields();

      this.markOptionalWarnings(missingFields);

      const canContinue = await this.confirmMissingOptionalFields();

      if (!canContinue) {
        return;
      }

      this.loading = true;

      try {
        const { data } = await ApiService.post(
          `sales/salesOrder/${this.salesOrderId}/generated-documents/${DOCUMENT_TYPE}/generate`,
          this.form
        );

        if (data && data.url) {
          window.open(data.url, "_blank");
        }

        this.$emit("generated", data);
        this.closeDialog();
      } catch (error) {
        console.error(error);

        Swal.fire({
          title: "Error",
          icon: "error",
          text: "The document could not be created.",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.technical-dialog-layout {
  display: flex;
  min-height: 70vh;
}

.technical-form-pane {
  flex: 0 0 100%;
  max-width: 100%;
  border-right: 1px solid #e0e0e0;
  overflow-y: auto;
}
.technical-form-pane.with-preview {
  flex: 0 0 40%;
  max-width: 40%;
}

.technical-preview-pane {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  background: #f5f5f5;
  padding: 16px;
  min-width: 0;
}

.technical-preview-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.technical-preview-frame {
  width: 100%;
  height: calc(70vh - 32px);
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
}

.technical-preview-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(70vh - 32px);
  background: #fff;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  color: #757575;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
}

.optional-warning-field >>> .v-input__slot {
  background-color: #fff8e1 !important;
}

.optional-warning-field >>> fieldset {
  border-color: #f9a825 !important;
}
</style>